// signals-web.ts — 1:1 JS port of src-tauri/src/signals.rs: the per-plant cadence engine and
// the cross-plant "This week" slice. 🔴 WHY: on web, compute_plant_signals was a ZERO SHELL
// (fleet zero-stub sweep 2026-08-19) — the plant Story narration had nothing to narrate — and
// compute_week_signals was a hand approximation that drifted from the Rust (no grace margin,
// no record-gap flag, no headline facts). Where this and the Rust could drift, the Rust wins.
//
// The fence travels with the math: every figure is an interval/gap/count of the user's OWN
// log. Observational only — nothing here advises, diagnoses, or judges plant health.

import type { Plant, CareEvent } from "./store";

const CADENCE_KINDS = ["water", "feed", "repot"] as const;
const WEEK_DAYS = 7;

const round1 = (x: number) => Math.round(x * 10) / 10;
const parseDay = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(d ?? "") ? d.slice(0, 10) : null);
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
const addDaysIso = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const dayWord = (n: number) => (n === 1 ? "day" : "days");

function gapsBetween(dates: string[]): number[] {
  const sorted = [...dates].sort();
  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const g = dayDiff(sorted[i - 1], sorted[i]);
    if (g > 0) gaps.push(g);
  }
  return gaps;
}

function intervalStats(dates: string[]): { avg: number; min: number; max: number } | null {
  const gaps = gapsBetween(dates);
  if (!gaps.length) return null;
  const sum = gaps.reduce((s, g) => s + g, 0);
  return { avg: round1(sum / gaps.length), min: Math.min(...gaps), max: Math.max(...gaps) };
}

function recentTrend(dates: string[], allTimeAvg: number | null): { recent: number | null; trend: string | null } {
  const gaps = gapsBetween(dates);
  if (gaps.length < 3) return { recent: null, trend: null };
  const recent3 = gaps.slice(-3);
  const recentAvg = round1(recent3.reduce((s, g) => s + g, 0) / recent3.length);
  let trend: string | null = null;
  if (allTimeAvg != null) {
    if (allTimeAvg <= 0) trend = "steady";
    else {
      const ratio = recentAvg / allTimeAvg;
      trend = ratio < 0.85 ? "tightening" : ratio > 1.15 ? "widening" : "steady";
    }
  }
  return { recent: recentAvg, trend };
}

export interface CadenceStatWeb {
  kind: string; events: number; last_on: string | null; days_since_last: number | null;
  avg_interval_days: number | null; recent_avg_interval_days: number | null; trend: string | null;
  min_interval_days: number | null; max_interval_days: number | null; over_under_days: number | null;
  past_personal_rhythm: boolean; longest_gap_this_year: boolean;
}

function cadenceForKind(kind: string, events: CareEvent[], today: string): CadenceStatWeb {
  const dates = events.filter((e) => e.kind === kind).map((e) => parseDay(e.done_on)).filter((d): d is string => !!d).sort();
  const lastOn = dates[dates.length - 1] ?? null;
  const daysSinceLast = lastOn != null ? dayDiff(lastOn, today) : null;
  const stats = intervalStats(dates);
  const avg = stats?.avg ?? null;
  const { recent, trend } = recentTrend(dates, avg);

  const overUnder = daysSinceLast != null && avg != null ? round1(daysSinceLast - avg) : null;
  let pastRhythm = false;
  if (daysSinceLast != null && avg != null) {
    const grace = Math.max(avg * 0.25, 2);
    pastRhythm = daysSinceLast > avg + grace;
  }

  let longestGapThisYear = false;
  if (lastOn != null && daysSinceLast != null && dates.length >= 1) {
    const yearAgo = addDaysIso(today, -365);
    let historicMax = 0;
    for (let i = 1; i < dates.length; i++) {
      if (dates[i] >= yearAgo) historicMax = Math.max(historicMax, dayDiff(dates[i - 1], dates[i]));
    }
    longestGapThisYear = daysSinceLast > 0 && daysSinceLast >= historicMax;
  }

  return {
    kind, events: dates.length, last_on: lastOn, days_since_last: daysSinceLast,
    avg_interval_days: avg, recent_avg_interval_days: recent, trend,
    min_interval_days: stats?.min ?? null, max_interval_days: stats?.max ?? null,
    over_under_days: overUnder, past_personal_rhythm: pastRhythm, longest_gap_this_year: longestGapThisYear,
  };
}

const kindActivity = (k: string) => (k === "water" ? "watering" : k === "feed" ? "feeding" : k === "repot" ? "repotting" : "care");
const kindVerbPast = (k: string) => (k === "water" ? "watered" : k === "feed" ? "fed" : k === "repot" ? "repotted" : "logged");

function buildPlantFacts(
  plantName: string, cadence: CadenceStatWeb[], totalEvents: number,
  firstEventOn: string | null, daysSincePotted: number | null,
  latestNote: { on: string; kind: string; text: string } | null,
): { kind: string; text: string; tone: string }[] {
  const facts: { kind: string; text: string; tone: string }[] = [];
  for (const c of cadence) {
    if (c.past_personal_rhythm && c.days_since_last != null && c.avg_interval_days != null) {
      const over = c.over_under_days ?? c.days_since_last - c.avg_interval_days;
      const overAbs = Math.abs(over);
      facts.push({ kind: "rhythm", tone: "watch", text: `It's been ${c.days_since_last} ${dayWord(c.days_since_last)} since you last ${kindVerbPast(c.kind)} it — about ${Math.round(overAbs)} ${Math.round(overAbs) === 1 ? "day" : "days"} past its own usual ~${Math.round(c.avg_interval_days)}-day ${kindActivity(c.kind)} gap.` });
    }
  }
  for (const c of cadence) {
    if (c.longest_gap_this_year && c.days_since_last != null) {
      facts.push({ kind: "gap", tone: "watch", text: `Its current ${c.days_since_last}-${dayWord(c.days_since_last)} gap is the longest ${kindActivity(c.kind)} gap you've logged for it this year.` });
    }
  }
  for (const c of cadence) {
    if (!c.past_personal_rhythm && c.days_since_last != null && c.avg_interval_days != null) {
      facts.push({ kind: "cadence", tone: "good", text: `Its ${kindActivity(c.kind)} is steady: you ${kindVerbPast(c.kind)} it on average every ~${Math.round(c.avg_interval_days)} days, and you're ${c.days_since_last} ${dayWord(c.days_since_last)} into the current gap.` });
    }
  }
  const w = cadence.find((c) => c.kind === "water");
  if (w && w.recent_avg_interval_days != null && w.avg_interval_days != null && w.trend && w.trend !== "steady") {
    const direction = w.trend === "tightening" ? "shorter" : "longer";
    facts.push({ kind: "cadence", tone: "neutral", text: `Your last few watering gaps have run ${direction} — about ~${Math.round(w.recent_avg_interval_days)} days vs your ~${Math.round(w.avg_interval_days)}-day all-time average.` });
  }
  if (w && w.min_interval_days != null && w.max_interval_days != null && w.max_interval_days > w.min_interval_days) {
    facts.push({ kind: "cadence", tone: "neutral", text: `Across all your waterings, the gap has run from ${w.min_interval_days} to ${w.max_interval_days} ${dayWord(w.max_interval_days)}.` });
  }
  if (totalEvents > 0 && firstEventOn) {
    facts.push({ kind: "span", tone: "neutral", text: `You've logged ${totalEvents} care ${totalEvents === 1 ? "event" : "events"} for ${plantName} since ${firstEventOn}.` });
  }
  if (daysSincePotted != null && daysSincePotted >= 0) {
    facts.push({ kind: "milestone", tone: "neutral", text: `It was last (re)potted ${daysSincePotted} ${dayWord(daysSincePotted)} ago.` });
  }
  if (latestNote) {
    facts.push({ kind: "note", tone: "neutral", text: `Your most recent note (on ${latestNote.on}) reads: “${latestNote.text}”.` });
  }
  return facts.slice(0, 6);
}

export function computePlantSignalsWeb(plant: Plant, events: CareEvent[], today: string) {
  const dated = events.map((e) => parseDay(e.done_on)).filter((d): d is string => !!d).sort();
  const first_event_on = dated[0] ?? null;
  const last_event_on = dated[dated.length - 1] ?? null;

  const cadence = CADENCE_KINDS.map((k) => cadenceForKind(k, events, today));

  let latest_note: { on: string; kind: string; text: string } | null = null;
  const withNotes = events.filter((e) => e.note.trim());
  withNotes.sort((a, b) => b.done_on.localeCompare(a.done_on) || b.created_at.localeCompare(a.created_at));
  if (withNotes[0]) {
    const e = withNotes[0];
    let text = e.note.trim();
    if ([...text].length > 280) text = [...text].slice(0, 280).join("") + "…";
    latest_note = { on: e.done_on.slice(0, 10), kind: e.kind, text };
  }

  const potted = plant.potted_on ? parseDay(plant.potted_on) : null;
  const days_since_potted = potted ? dayDiff(potted, today) : null;

  return {
    schema:
      "oriora.plant.v1-plant-signals: derived cadence aggregates for ONE plant (no raw timeline). Each cadence block is days-since-last vs the plant's OWN average interval (its personal rhythm). over_under_days>0 and past_personal_rhythm=true mean it is past its usual gap. recent_avg_interval_days is the last-3-gaps average and trend (tightening|widening|steady) compares it to the all-time average — describe the shift, do not advise about it. latest_note is the single most-recent free-text note. Every figure is a plain observation of the user's own log — describe the numbers, NEVER advise, diagnose, suggest treatment, or judge plant health.",
    generated_for: today,
    plant_id: plant.id,
    plant_name: plant.name,
    species: plant.species,
    location: plant.location,
    potted_on: plant.potted_on,
    days_since_potted,
    total_events: events.length,
    first_event_on,
    last_event_on,
    cadence,
    latest_note,
    headline_facts: buildPlantFacts(plant.name, cadence, events.length, first_event_on, days_since_potted, latest_note),
  };
}

export function computeWeekSignalsWeb(plants: Plant[], allEvents: CareEvent[], today: string) {
  const weekStart = addDaysIso(today, -(WEEK_DAYS - 1));
  let totalWeekEvents = 0;
  let pastCount = 0;

  const rows = plants.map((p) => {
    const pEvents = allEvents.filter((e) => e.plant_id === p.id);
    const eventsThisWeek = pEvents.filter((e) => {
      const d = parseDay(e.done_on);
      return d != null && d >= weekStart && d <= today;
    }).length;
    totalWeekEvents += eventsThisWeek;
    const water = cadenceForKind("water", pEvents, today);
    if (water.past_personal_rhythm) pastCount++;
    return {
      plant_id: p.id, name: p.name, species: p.species,
      days_since_water: water.days_since_last,
      avg_water_interval_days: water.avg_interval_days,
      water_over_under_days: water.over_under_days,
      past_water_rhythm: water.past_personal_rhythm,
      longest_water_gap_this_year: water.longest_gap_this_year,
      events_this_week: eventsThisWeek,
    };
  });
  rows.sort((a, b) => (b.water_over_under_days ?? -Infinity) - (a.water_over_under_days ?? -Infinity));

  const facts: { kind: string; text: string; tone: string }[] = [];
  for (const r of rows.filter((r) => r.past_water_rhythm)) {
    if (r.days_since_water != null && r.avg_water_interval_days != null) {
      const over = r.water_over_under_days ?? r.days_since_water - r.avg_water_interval_days;
      const overAbs = Math.abs(over);
      facts.push({ kind: "rhythm", tone: "watch", text: `${r.name} is at day ${r.days_since_water} since its last watering — about ${Math.round(overAbs)} ${Math.round(overAbs) === 1 ? "day" : "days"} past its own usual ~${Math.round(r.avg_water_interval_days)}-day rhythm.` });
    }
  }
  for (const r of rows.filter((r) => r.longest_water_gap_this_year)) {
    if (r.days_since_water != null) {
      facts.push({ kind: "gap", tone: "watch", text: `${r.name}'s ${r.days_since_water}-${dayWord(r.days_since_water)} gap is its longest watering gap this year.` });
    }
  }
  const total = rows.length;
  if (total > 0) {
    facts.push(pastCount === 0
      ? { kind: "rhythm", tone: "good", text: `All ${total} of your plants are on their usual watering rhythm right now.` }
      : { kind: "rhythm", tone: "watch", text: `${pastCount} of your ${total} plants ${pastCount === 1 ? "is" : "are"} past their usual watering rhythm; the rest are on pace.` });
  }
  facts.push({ kind: "span", tone: "neutral", text: `You logged ${totalWeekEvents} care ${totalWeekEvents === 1 ? "event" : "events"} across your collection in the last 7 days.` });
  const steadyCands = rows.filter((r) => !r.past_water_rhythm && r.avg_water_interval_days != null && r.days_since_water != null);
  if (steadyCands.length) {
    const steady = steadyCands.reduce((a, b) =>
      Math.abs(b.water_over_under_days ?? Infinity) < Math.abs(a.water_over_under_days ?? Infinity) ? b : a);
    if (steady.avg_water_interval_days != null) {
      facts.push({ kind: "cadence", tone: "good", text: `${steady.name} is your steadiest — watered like clockwork on its ~${Math.round(steady.avg_water_interval_days)}-day rhythm.` });
    }
  }

  return {
    schema:
      "oriora.plant.v1-week-signals: per-plant watering cadence across the user's whole collection. days_since_water vs each plant's OWN avg interval (personal rhythm); past_water_rhythm=true means it is past its usual gap; longest_water_gap_this_year flags a record gap. Rows are ordered most-overdue first. Plain observations of the user's own log — describe the numbers, NEVER advise, diagnose, suggest treatment, or judge plant health.",
    generated_for: today,
    plant_count: plants.length,
    events_this_week: totalWeekEvents,
    plants_past_water_rhythm: pastCount,
    plants: rows,
    headline_facts: facts.slice(0, 6),
  };
}
